import { useState } from 'react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import ThemeToggle from '../../components/common/ThemeToggle';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { tenantAPI, authAPI } from '../../services/api';

export default function SettingsPage() {
  const { user, tenant, updateTenant, checkAuth, logout } = useAuth();
  const { theme } = useTheme();
  const [workspaceName, setWorkspaceName] = useState(tenant?.name || '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  // Profile edit state
  const [editName, setEditName] = useState(user?.name || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileSuccess(false);
    setProfileError('');

    try {
      await authAPI.updateProfile({ name: editName });
      if (checkAuth) await checkAuth();
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (error) {
      setProfileError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    setSavingPassword(true);
    try {
      await authAPI.changePassword({ currentPassword, newPassword });
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveWorkspace = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);

    try {
      const response = await tenantAPI.update({ name: workspaceName });
      updateTenant(response.data);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to update workspace:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Please enter your password');
      return;
    }

    setDeleting(true);
    setDeleteError('');

    try {
      await authAPI.deleteAccount(deletePassword);
      // Clear local storage and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    } catch (error) {
      console.error('Failed to delete account:', error);
      const message = error.response?.data?.error || 'Failed to delete account';
      setDeleteError(message);
      setDeleting(false);
    }
  };

  const openDeleteModal = () => {
    setShowDeleteModal(true);
    setDeletePassword('');
    setDeleteError('');
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeleteError('');
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Settings</h1>
        <p className="mt-1 text-slate-500 dark:text-slate-400">
          Manage your account and workspace settings.
        </p>
      </div>

      {/* Account Settings */}
      <Card>
        <Card.Header>
          <Card.Title>Account Information</Card.Title>
          <Card.Description>Update your personal account details.</Card.Description>
        </Card.Header>

        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Your name"
            />
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Email</p>
              <p className="font-medium text-slate-900 dark:text-white mt-1">{user?.email}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Role</p>
              <p className="font-medium text-slate-900 dark:text-white capitalize mt-1">{user?.role}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Member since</p>
              <p className="font-medium text-slate-900 dark:text-white mt-1">
                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
          {profileError && (
            <p className="text-sm text-red-600 dark:text-red-400">{profileError}</p>
          )}
          <div className="flex items-center gap-4">
            <Button type="submit" loading={savingProfile}>
              Update Profile
            </Button>
            {profileSuccess && (
              <span className="text-sm text-green-600 dark:text-green-400">
                Profile updated!
              </span>
            )}
          </div>
        </form>
      </Card>

      {/* Change Password */}
      <Card>
        <Card.Header>
          <Card.Title>Change Password</Card.Title>
          <Card.Description>Update your account password.</Card.Description>
        </Card.Header>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <Input
            type="password"
            label="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter current password"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
            <Input
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
          </div>
          {passwordError && (
            <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>
          )}
          <div className="flex items-center gap-4">
            <Button type="submit" loading={savingPassword}>
              Update Password
            </Button>
            {passwordSuccess && (
              <span className="text-sm text-green-600 dark:text-green-400">
                Password updated!
              </span>
            )}
          </div>
        </form>
      </Card>

      {/* Workspace Settings */}
      <Card>
        <Card.Header>
          <Card.Title>Workspace Settings</Card.Title>
          <Card.Description>Manage your workspace configuration.</Card.Description>
        </Card.Header>

        <form onSubmit={handleSaveWorkspace} className="space-y-4">
          <Input
            label="Workspace Name"
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            placeholder="My Workspace"
          />

          <div className="flex items-center gap-4">
            <Button type="submit" loading={saving}>
              Save Changes
            </Button>
            {success && (
              <span className="text-sm text-green-600 dark:text-green-400">
                Saved successfully!
              </span>
            )}
          </div>
        </form>
      </Card>

      {/* Appearance */}
      <Card>
        <Card.Header>
          <Card.Title>Appearance</Card.Title>
          <Card.Description>Customize how the app looks.</Card.Description>
        </Card.Header>

        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-slate-900 dark:text-white">Theme</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Currently using {theme} mode
            </p>
          </div>
          <ThemeToggle />
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200 dark:border-red-900">
        <Card.Header>
          <Card.Title className="text-red-600 dark:text-red-400">Danger Zone</Card.Title>
          <Card.Description>Irreversible actions for your account.</Card.Description>
        </Card.Header>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900 dark:text-white">Sign out</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Sign out of your account on this device.
              </p>
            </div>
            <Button variant="outline" onClick={logout}>
              Sign Out
            </Button>
          </div>

          <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Delete Account</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {user?.role === 'owner'
                    ? 'Permanently delete your account, workspace, and all data.'
                    : 'Permanently delete your account and leave this workspace.'}
                </p>
              </div>
              <Button variant="danger" onClick={openDeleteModal}>
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeDeleteModal}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              Delete Account
            </h3>

            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {user?.role === 'owner' ? (
                <>
                  This will permanently delete your account, your workspace
                  <strong className="text-slate-900 dark:text-white"> "{tenant?.name}"</strong>,
                  and all associated data including team members, integrations, and reports.
                  This action cannot be undone.
                </>
              ) : (
                <>
                  This will permanently delete your account and remove you from the workspace.
                  This action cannot be undone.
                </>
              )}
            </p>

            <div className="mb-4">
              <Input
                type="password"
                label="Enter your password to confirm"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Your password"
                error={deleteError}
              />
            </div>

            {deleteError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">{deleteError}</p>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAccount}
                loading={deleting}
              >
                Delete My Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
